"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { NAV_LINKS, PARTNER_PANEL_URL, WHATSAPP_URL } from "@/lib/site";

function WhatsAppIcon({ className = "h-[18px] w-[18px]" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`sticky top-0 z-50 text-paper transition-colors duration-300 ${
        scrolled || open ? "border-b border-white/10 bg-ink backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 lg:h-20 lg:px-10">
        <Link href="/" aria-label="Shoppub — início" className="shrink-0">
          <Image
            src="/brand/logo_shoppub_branco.svg"
            alt="Shoppub"
            width={128}
            height={36}
            priority
            className="h-8 w-auto lg:h-9"
          />
        </Link>

        <nav
          className="hidden items-center gap-0.5 rounded-full border border-white/10 bg-white/5 p-1 backdrop-blur lg:flex"
          aria-label="Principal"
        >
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className="rounded-full px-4 py-2 text-[14px] font-medium text-paper/70 transition hover:bg-white/10 hover:text-paper"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <a
            href={PARTNER_PANEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden whitespace-nowrap px-2 text-[14px] font-medium text-paper/70 transition hover:text-paper xl:inline-block"
          >
            Painel do parceiro
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/15 text-paper/80 transition hover:border-lime hover:text-lime lg:flex"
          >
            <WhatsAppIcon />
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-full bg-lime px-5 py-2.5 text-[14px] font-bold text-ink transition hover:bg-lime-deep sm:inline-block"
          >
            Falar com um especialista
          </a>
          <button
            type="button"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 flex-col items-center justify-center gap-[5px] rounded-full border border-white/20 lg:hidden"
          >
            <span
              className={`h-[2px] w-4 rounded bg-paper transition-transform ${
                open ? "translate-y-[3.5px] rotate-45" : ""
              }`}
            />
            <span
              className={`h-[2px] w-4 rounded bg-paper transition-transform ${
                open ? "-translate-y-[3.5px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* menu mobile */}
      {open && (
        /* o backdrop-blur do header vira containing block p/ fixed:
           bottom-0 colapsaria — usar altura explícita da viewport */
        <div className="fixed inset-x-0 top-16 z-40 flex h-[calc(100dvh-4rem)] flex-col bg-ink px-5 pb-8 pt-6 lg:hidden">
          <nav className="flex flex-col" aria-label="Menu">
            {NAV_LINKS.map((l, i) => (
              <a
                key={l.label}
                href={l.href}
                {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                onClick={() => setOpen(false)}
                className="flex items-baseline gap-4 border-b border-white/10 py-5 font-display text-3xl font-bold text-paper"
              >
                <span className="font-sans text-xs font-semibold text-smoke-dark">
                  0{i + 1}
                </span>
                {l.label}
              </a>
            ))}
          </nav>
          <a
            href={PARTNER_PANEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto flex items-center justify-between border-t border-white/10 py-5 text-lg font-semibold text-paper/85"
          >
            Painel do parceiro
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-smoke-dark" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 17L17 7M9 7h8v8" />
            </svg>
          </a>
          <div className="flex items-center gap-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 rounded-full bg-lime py-4 text-center text-lg font-bold text-ink"
            >
              Falar com um especialista
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full border border-white/20 text-paper"
            >
              <WhatsAppIcon className="h-6 w-6" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
